import { Chess, PieceSymbol } from 'chess.js';
import { Mission, MissionStatus, getMissionStatus } from '../types/mission';
import { ChessPiece, PieceType } from 'types/chess';
import { KING_IN_CHECK_FALLBACK } from 'services/chess/moveGenerators';
import { sequenceMissions, getAllSequenceMissions } from './sequenceMissions';

/**
 * Initial missions data for the Chess Codebreaker game
 */
const missions: Mission[] = [
  // Stage 1: Introduction to Coordinates
  {
    id: 'coordinates-101',
    title: 'Operation Chessboard',
    description: 'Learn the basic coordinate system of the chessboard',
    stage: 1,
    type: 'coordinate',
    difficulty: 1,
    timeLimit: null,
    prerequisites: [],
    briefing: 'Welcome, new recruit! Your first mission is to familiarize yourself with the chessboard coordinate system. This is the foundation of all chess communication.',
    objective: 'Correctly identify 5 coordinates on the chessboard',
    successCriteria: 'Identify at least 4 out of 5 coordinates correctly',
    rewards: {
      experience: 100,
      skillPoints: {
        coordinates: 2,
        basicMoves: 0,
        sequences: 0,
        gameAnalysis: 0,
      },
    },
  },
  {
    id: 'coordinates-102',
    title: 'Field Training',
    description: 'Practice identifying coordinates under time pressure',
    stage: 1,
    type: 'coordinate',
    difficulty: 2,
    timeLimit: 30,
    prerequisites: ['coordinates-101'],
    briefing: 'Good work on the basics. Now we need to test your speed. In the field, you\'ll need to react quickly to changing situations.',
    objective: 'Correctly identify 10 coordinates with a time limit',
    successCriteria: 'Identify at least 7 out of 10 coordinates correctly within the time limit',
    rewards: {
      experience: 150,
      skillPoints: {
        coordinates: 3,
        basicMoves: 0,
        sequences: 0,
        gameAnalysis: 0,
      },
    },
  },
  {
    id: 'coordinates-103',
    title: 'Advanced Grid Navigation',
    description: 'Master the coordinate system with challenging exercises',
    stage: 1,
    type: 'coordinate',
    difficulty: 3,
    timeLimit: 45,
    prerequisites: ['coordinates-102'],
    briefing: 'You\'re showing promise, agent. It\'s time to push your skills further. This exercise will test your mastery of the coordinate system.',
    objective: 'Correctly identify 15 coordinates with time pressure and distractions',
    successCriteria: 'Identify at least 12 out of 15 coordinates correctly within the time limit',
    rewards: {
      experience: 200,
      skillPoints: {
        coordinates: 4,
        basicMoves: 1,
        sequences: 0,
        gameAnalysis: 0,
      },
    },
  },
  
  // Stage 2: Basic Piece Movements
  {
    id: 'piece-movement-101',
    title: 'Operative Training: Pawns & Knights',
    description: 'Learn how pawns and knights move on the board',
    stage: 2,
    type: 'move',
    difficulty: 2,
    timeLimit: 60,
    prerequisites: ['coordinates-103'],
    briefing: 'Congratulations on your promotion to Stage 2! Now we\'ll learn about the chess pieces themselves, starting with pawns and knights. These are critical components in your intelligence toolkit.',
    objective: 'Correctly identify legal moves for pawns and knights in 8 different positions',
    successCriteria: 'Identify at least 6 out of 8 legal moves correctly',
    rewards: {
      experience: 250,
      skillPoints: {
        coordinates: 1,
        basicMoves: 3,
        sequences: 0,
        gameAnalysis: 0,
      },
    },
    position: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
    targetMove: 'e4',
    pieces: ['p','n']
  },
  {
    id: 'piece-movement-102',
    title: 'Operative Training: Bishops & Rooks',
    description: 'Learn how bishops and rooks move on the board',
    stage: 2,
    type: 'move',
    difficulty: 3,
    timeLimit: 60,
    prerequisites: ['piece-movement-101'],
    briefing: 'Time to expand your knowledge, agent. Bishops and rooks provide long-range capabilities that are essential for strategic operations.',
    objective: 'Correctly identify legal moves for bishops and rooks in 10 different positions',
    successCriteria: 'Identify at least 8 out of 10 legal moves correctly within the time limit',
    rewards: {
      experience: 300,
      skillPoints: {
        coordinates: 1,
        basicMoves: 4,
        sequences: 0,
        gameAnalysis: 0,
      },
    },
    position: 'rnbqkbnr/pppppppp/8/8/4P3/8/1PPP1PPP/RNBQKBNR w KQkq - 0 2',
    targetMove: 'Ra3',
    pieces: ['b','r']
  },
  {
    id: 'piece-movement-103',
    title: 'Tactical Strike',
    description: 'Your opponent has left a piece undefended. Take advantage of this tactical opportunity and capture the piece. Always be on the lookout for free material!',
    stage: 2,
    type: 'move',
    difficulty: 2,
    timeLimit: 60,
    prerequisites: ['piece-movement-102'],
    briefing: 'A tactical opportunity has presented itself. Analyze the board and seize the advantage.',
    objective: 'Capture the undefended piece.',
    successCriteria: 'Capture the piece in one move.',
    rewards: {
      experience: 100,
      skillPoints: {
        coordinates: 0,
        basicMoves: 1,
        sequences: 1,
        gameAnalysis: 0
      },
    },
    position: 'rnbqkb1r/pppp1ppp/8/4p3/4n3/5N2/PPPPBPPP/RNBQK2R w KQkq - 0 4',
    targetMove: 'Nxe5',
    focus: 'tactics',
  },
  {
    id: 'piece-movement-104',
    title: 'Direct Threat',
    description: 'Agent, it\'s time to put the enemy king under direct threat. Find the move that puts the opponent\'s king in check or checkmate. Remember: a check forces your opponent to respond immediately.',
    stage: 2,
    type: 'move',
    difficulty: 2,
    timeLimit: 90,
    prerequisites: ['piece-movement-103'],
    briefing: 'Force your opponent onto the defensive by delivering a check or checkmate.',
    objective: 'Put the opponent\'s king in check or checkmate.',
    successCriteria: 'Deliver check or checkmate in one move.',
    rewards: {
      experience: 125,
      skillPoints: {
        coordinates: 0,
        basicMoves: 1,
        sequences: 1,
        gameAnalysis: 1
      },
    },
    position: 'rnbqkbnr/ppp2ppp/8/3p4/8/5N2/PPPP1PPP/RNBQKB1R w KQkq - 0 3',
    targetMove: 'Qe2',
    focus: 'check',
  },
  {
    id: 'piece-movement-105',
    title: 'Defensive Maneuvers',
    description: 'Your king is under attack! Find the best defensive move to protect your king. This is a critical situation requiring your immediate attention. Consider all your defensive options carefully.',
    stage: 2,
    type: 'move',
    difficulty: 3,
    timeLimit: 120,
    prerequisites: ['piece-movement-104'],
    briefing: 'King safety is paramount. Address the immediate threat to your king.',
    objective: 'Defend your king from check.',
    successCriteria: 'Successfully defend the king in one move.',
    rewards: {
      experience: 150,
      skillPoints: {
        coordinates: 0,
        basicMoves: 2,
        sequences: 0,
        gameAnalysis: 2
      },
    },
    position: KING_IN_CHECK_FALLBACK.position,
    targetMove: KING_IN_CHECK_FALLBACK.targetMove,
    focus: 'defense',
  },
];

/**
 * Get all missions in the game
 * 
 * @returns Array of all missions
 */
export const getAllMissions = (): Mission[] => {
  return [...missions, ...getAllSequenceMissions()];
};

/**
 * Get a specific mission by ID
 * 
 * @param missionId ID of the mission to retrieve
 * @returns The mission with the specified ID or undefined if not found
 */
export const getMission = (missionId: string): Mission | undefined => {
  // First check in the main missions array
  const mainMission = missions.find(mission => mission.id === missionId);
  if (mainMission) return mainMission;
  
  // If not found, check in sequence missions
  return sequenceMissions.find(mission => mission.id === missionId);
};

/**
 * Get all missions for a specific stage
 * 
 * @param stage Stage number
 * @returns Array of missions in the specified stage
 */
export const getMissionsByStage = (stage: number): Mission[] => {
  const allMissions = getAllMissions();
  return allMissions.filter(mission => mission.stage === stage);
};

/**
 * Get available missions based on user's completed missions
 * 
 * @param completedMissions Array of completed mission IDs
 * @returns Array of available missions (where all prerequisites are completed)
 */
export const getAvailableMissions = (completedMissions: string[]): Mission[] => {
  const allMissions = getAllMissions();
  return allMissions.filter(mission => {
    const status = getMissionStatus(mission, completedMissions);
    return status === 'available';
  });
};

/**
 * Get all missions with their status based on user's completed missions
 * 
 * @param completedMissions Array of completed mission IDs
 * @returns Array of objects containing mission and status
 */
export const getMissionsWithStatus = (completedMissions: string[]): Array<{ mission: Mission, status: MissionStatus }> => {
  const allMissions = getAllMissions();
  return allMissions.map(mission => ({
    mission,
    status: getMissionStatus(mission, completedMissions)
  }));
};

export default {
  getAllMissions,
  getMission,
  getMissionsByStage,
  getAvailableMissions,
  getMissionsWithStatus
}; 